import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ASSETS, TRANSITION } from '../lib/constants';
import { useReducedMotion } from './useReducedMotion';

/**
 * Cinematic transition orchestrator.
 *
 * Mounts a fade-to-black overlay + the two transition videos at the body root.
 * Exposes `trigger({ to, video, title })` which runs the multi-phase choreography:
 *
 *   T+0       click → cards/headline drop animation begins (CSS-driven via body class)
 *   T+900     selected video starts playing; image→video crossfade
 *   T+6500    fade-to-black overlay engages
 *   T+8500    react-router navigate(); wake-up blur applied; overlay clears
 *   T+10200   cleanup (classes removed, videos paused, transitioning flag released)
 *
 * Also renders a "Skip animation" button at body-fixed bottom-right while a
 * cinematic is playing — fades in after a short delay so it doesn't fight the
 * intro, then becomes available for the user to short-circuit the sequence.
 *
 * Each feature route (arena, worldview) is responsible only for its destination scene.
 */

type VideoKey = 'arena' | 'worldview';

interface TransitionConfig {
  to: string;
  video: VideoKey;
  title?: string;
}

interface FadeConfig {
  to: string;
  title?: string;
}

interface TransitionContextValue {
  /** Full cinematic transition: drop, video, fade-to-black, wake-up. Used from the landing entry. */
  trigger: (config: TransitionConfig) => void;
  /** Lightweight fade transition: fade-to-black, navigate, fade-from-black. Used inside the experience. */
  fadeTo: (config: FadeConfig) => void;
}

const Ctx = createContext<TransitionContextValue | null>(null);

export function useTransition(): TransitionContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTransition must be used inside <TransitionProvider>');
  return ctx;
}

const VIDEO_SRC: Record<VideoKey, string> = {
  arena: ASSETS.transitionArena,
  worldview: ASSETS.transitionWorldview,
};

export function TransitionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const arenaVideoRef = useRef<HTMLVideoElement>(null);
  const worldviewVideoRef = useRef<HTMLVideoElement>(null);
  const fadeoutRef = useRef<HTMLDivElement>(null);
  const transitioningRef = useRef(false);

  /** True while a cinematic `trigger()` is in flight — drives the Skip button. */
  const [isCinematicActive, setIsCinematicActive] = useState(false);

  /** The skip function for the in-flight cinematic. Cleared once finished. */
  const skipRef = useRef<(() => void) | null>(null);

  const trigger = useCallback(
    (config: TransitionConfig) => {
      if (transitioningRef.current) return;

      if (reduceMotion) {
        navigate(config.to);
        if (config.title) document.title = config.title;
        return;
      }

      transitioningRef.current = true;
      setIsCinematicActive(true);
      const videoRef = config.video === 'arena' ? arenaVideoRef : worldviewVideoRef;
      const timeouts: number[] = [];

      // Finalize the transition — used by both natural phase-4 and Skip.
      // Idempotent: safe to call once.
      let finalized = false;
      const finalize = () => {
        if (finalized) return;
        finalized = true;

        // Cancel anything still pending.
        timeouts.forEach((t) => clearTimeout(t));

        // Snap the video out of the render tree before the black overlay fades,
        // so it can't bleed back into view.
        [arenaVideoRef.current, worldviewVideoRef.current].forEach((v) => {
          if (!v) return;
          try {
            v.pause();
          } catch {
            /* noop */
          }
          v.style.display = 'none';
        });

        navigate(config.to);
        if (config.title) document.title = config.title;

        // Make sure the overlay is at full-black before we begin the wake-up
        // fade, regardless of how far phase 3 had progressed when Skip was hit.
        fadeoutRef.current?.classList.add('is-active');

        document.documentElement.classList.add('is-arena-waking');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fadeoutRef.current?.classList.remove('is-active');
            document.documentElement.classList.add('is-arena-cleared');
          });
        });

        window.setTimeout(() => {
          document.body.classList.remove('is-transitioning', 'is-video-active');
          document.documentElement.classList.remove('is-arena-waking', 'is-arena-cleared');
          [arenaVideoRef.current, worldviewVideoRef.current].forEach((v) => {
            if (!v) return;
            v.classList.remove('is-playing');
            v.style.display = '';
            try {
              v.currentTime = 0;
            } catch {
              /* noop */
            }
          });
          transitioningRef.current = false;
          skipRef.current = null;
          setIsCinematicActive(false);
        }, TRANSITION.WAKE_CLEANUP_MS);
      };

      // Expose skip for the button to call.
      skipRef.current = finalize;

      // Phase 1: drop animation (CSS handles it via body class).
      document.body.classList.add('is-transitioning');

      // Phase 2: start video + crossfade.
      timeouts.push(
        window.setTimeout(() => {
          const video = videoRef.current;
          if (video) {
            try {
              video.currentTime = 0;
              video.playbackRate = 1.0;
            } catch {
              /* noop */
            }
            video.classList.add('is-playing');
            video.play().catch(() => {});
          }
          document.body.classList.add('is-video-active');
        }, TRANSITION.DROP_DURATION_MS),
      );

      // Phase 3: fade-to-black.
      timeouts.push(
        window.setTimeout(() => {
          fadeoutRef.current?.classList.add('is-active');
        }, TRANSITION.DROP_DURATION_MS + TRANSITION.VIDEO_DURATION_MS),
      );

      // Phase 4: navigate and wake-up.
      timeouts.push(
        window.setTimeout(
          finalize,
          TRANSITION.DROP_DURATION_MS +
            TRANSITION.VIDEO_DURATION_MS +
            TRANSITION.FADE_TO_BLACK_MS,
        ),
      );
    },
    [navigate, reduceMotion],
  );

  const fadeTo = useCallback(
    (config: FadeConfig) => {
      if (transitioningRef.current) return;

      if (reduceMotion) {
        navigate(config.to);
        if (config.title) document.title = config.title;
        return;
      }

      const el = fadeoutRef.current;
      if (!el) {
        navigate(config.to);
        if (config.title) document.title = config.title;
        return;
      }

      transitioningRef.current = true;

      // Phase 1: fade to black (fast variant, ~700ms).
      el.classList.add('is-fast', 'is-active');

      // Phase 2: once fully black, navigate and fade back.
      window.setTimeout(() => {
        navigate(config.to);
        if (config.title) document.title = config.title;
        window.scrollTo(0, 0);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.classList.remove('is-active');
          });
        });

        // Cleanup once the fade-out finishes.
        window.setTimeout(() => {
          el.classList.remove('is-fast');
          transitioningRef.current = false;
        }, 650);
      }, 720);
    },
    [navigate, reduceMotion],
  );

  const handleSkip = useCallback(() => {
    skipRef.current?.();
  }, []);

  const value = useMemo(() => ({ trigger, fadeTo }), [trigger, fadeTo]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <video
        ref={arenaVideoRef}
        className="hero__video hero__video--arena"
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      >
        <source src={VIDEO_SRC.arena} type="video/mp4" />
      </video>
      <video
        ref={worldviewVideoRef}
        className="hero__video hero__video--worldview"
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      >
        <source src={VIDEO_SRC.worldview} type="video/mp4" />
      </video>
      <div ref={fadeoutRef} className="transition-fadeout" aria-hidden="true" />
      {isCinematicActive && (
        <button type="button" className="transition-skip" onClick={handleSkip}>
          <span className="transition-skip__label">Skip animation</span>
          <span aria-hidden="true" className="transition-skip__arrow">
            →
          </span>
        </button>
      )}
    </Ctx.Provider>
  );
}
